import { createConfiguration, dimensionLabel, scrollToConfiguration } from '@theme/directjantes-vehicle-configuration';

function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

function configurationPanel(root, suffix, copy) {
  const grid = root.querySelector('.dj-product-data-grid');
  const specs = grid?.querySelector('.dj-product-spec-card');
  const dimensions = grid?.querySelector('.dj-product-dimensions');
  const validRows = [...(dimensions?.querySelectorAll('tbody tr[data-dj-handle]') || [])]
    .some(row => row.dataset.djHandle && Number(String(row.dataset.djWidth || '').replace(',', '.')) > 0);
  if (!grid || !validRows) return null;
  const details = element('details', 'dj-vp-accordion dj-vc-block');
  details.open = true;
  const summary = element('summary', 'dj-vp-summary');
  summary.id = `dj-dp-heading-${suffix}`;
  const heading = element('h2', '', copy.technical_heading);
  const icon = dimensions.querySelector('h2 svg')?.cloneNode(true);
  if (icon) heading.prepend(icon);
  const chevron = element('span', 'dj-vp-chevron');
  chevron.setAttribute('aria-hidden', 'true');
  summary.append(heading, chevron);
  const panel = element('div', 'dj-vc-columns');
  panel.id = `dj-dp-panel-${suffix}`;
  panel.setAttribute('aria-labelledby', summary.id);
  if (specs) panel.append(specs);
  panel.append(dimensions);
  details.append(summary, panel);
  grid.replaceChildren(details);
  grid.classList.add('dj-vc-unified');
  return { details, summary, panel, specs, dimensions };
}

function orderPurchaseBlocks(root, form) {
  const purchase = form.closest('.buy-buttons-block');
  const parent = purchase?.parentElement;
  if (!parent) return;
  for (const selector of ['.dj-product-assistance', '.dj-product-shipping']) {
    let block = root.querySelector(selector);
    if (!block || !parent.contains(block)) continue;
    while (block.parentElement !== parent) block = block.parentElement;
    if (block !== purchase) parent.insertBefore(block, purchase);
  }
  parent.classList.add('dj-vp-purchase-content');
}

function prepareMedia(root, copy) {
  const media = root.querySelector('.product-information__media');
  if (!media) return;
  const caption = element('p', 'dj-vp-photo-caption', copy.caption);
  const certifications = media.querySelector('.dj-vp-certifications');
  if (certifications) {
    certifications.before(caption);
    certifications.hidden = false;
  } else media.append(caption);
  const hint = media.querySelector('.dj-product-photo-hint');
  const frames = [...media.querySelectorAll('media-gallery .product-media-container--image')]
    .filter(frame => !frame.closest('.dialog-zoomed-gallery'));
  if (hint && frames.length) {
    for (const frame of frames) frame.append(hint.cloneNode(true));
    hint.remove();
  }
  media.classList.add('dj-vp-captioned-media');
}

function deliveredTotal(root, form, copy) {
  const shipping = root.querySelector('directjantes-product-shipping');
  const purchase = form.closest('.buy-buttons-block');
  if (!purchase) return () => {};
  const box = element('div', 'dj-vp-total');
  box.setAttribute('role', 'status');
  box.setAttribute('aria-live', 'polite');
  box.setAttribute('aria-atomic', 'true');
  const text = element('div', 'dj-vp-total-copy');
  const label = element('strong', 'dj-vp-total-label', copy.total_selection);
  const note = element('span', 'dj-vp-total-note', copy.choose_total);
  const amount = element('strong', 'dj-vp-total-amount');
  amount.hidden = true;
  text.append(label, note); box.append(text, amount);
  if (shipping) { shipping.classList.add('dj-vp-shipping-total'); shipping.append(box); }
  else purchase.before(box);
  const write = (node, value) => { if (node.textContent !== value) node.textContent = value; };
  const cents = value => /^\d+$/.test(value || '') && Number.isSafeInteger(Number(value)) ? Number(value) : null;
  const update = () => {
    const committed = root.hasAttribute('data-dj-manual-committed');
    const subtotal = committed ? cents(root.dataset.djManualSubtotalCents) : null;
    const rate = cents(shipping?.dataset.unitCents);
    const total = subtotal !== null && rate !== null && rate > 0 ? subtotal + rate * 4 : null;
    const ready = total !== null && Number.isSafeInteger(total);
    write(label, subtotal !== null ? copy.total_for.replace('__count__', '4') : copy.total_selection);
    write(note, ready ? copy.included : subtotal !== null ? copy.shipping_pending : copy.choose_total);
    amount.hidden = !ready;
    if (ready) write(amount, `${(total / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € HT`);
    const shippingSelection = shipping?.querySelector('[data-dj-shipping-selection]');
    if (shippingSelection) shippingSelection.hidden = !ready;
  };
  const observer = new MutationObserver(() => {
    if (!root.isConnected) { observer.disconnect(); return; }
    update();
  });
  observer.observe(root, { attributes: true, attributeFilter: ['data-dj-manual-committed', 'data-dj-manual-subtotal-cents'] });
  if (shipping) observer.observe(shipping, { attributes: true, attributeFilter: ['data-unit-cents'] });
  for (const event of ['input', 'change', 'quantity-selector:update']) form.addEventListener(event, update);
  update();
  return update;
}

function init(root) {
  if (root.dataset.djDimensionProductReady || new URL(location.href).searchParams.get('dj_vehicle') === '1') return;
  const options = root.querySelector('.dj-product-options');
  const lots = options?.querySelector('directjantes-product-quantity');
  const form = root.querySelector('.product-details product-form-component form');
  const copySource = root.querySelector('[data-dj-dimension-copy]');
  if (!options || !lots || !form || !copySource) return;
  let copy;
  try { copy = JSON.parse(copySource.textContent); } catch { return; }
  const configuration = configurationPanel(root, form.id || 'product', copy);
  if (!configuration) return;
  root.dataset.djDimensionProductReady = 'true';
  root.dataset.djMountMode = 'manual';
  root.classList.add('dj-dimension-product');
  lots.remove();
  const geometry = root.querySelector('.dj-product-heading__geometry');
  const initialGeometry = [...(geometry?.children || [])].map(node => node.textContent.trim()).join(' · ');
  if (geometry?.dataset.djDiameter && geometry.dataset.djPcd) {
    geometry.replaceChildren(element('strong', 'dj-vp-diameter', `${geometry.dataset.djDiameter} ${copy.inches}`),
      element('span', 'dj-vp-pcd', `${copy.pcd} ${geometry.dataset.djPcd.replaceAll('x', '×')}`));
  }
  const guide = element('div', 'dj-dp-guide');
  guide.append(element('p', 'dj-product-label', copy.configuration));
  const button = element('button', 'dj-dp-guide-button'); button.type = 'button';
  button.setAttribute('aria-controls', configuration.panel.id);
  const guideText = element('span');
  const guideNote = element('small', '', copy.guide);
  guideText.append(element('strong', '', copy.choose_dimensions), guideNote);
  const arrow = element('span', 'dj-vp-chevron'); arrow.setAttribute('aria-hidden', 'true');
  button.append(guideText, arrow); guide.append(button); options.append(guide);
  button.addEventListener('click', () => {
    configuration.details.open = true;
    const target = configuration.dimensions.querySelector('.dj-vc-column-title') || configuration.summary;
    target.tabIndex = -1; scrollToConfiguration(target);
  });
  const assurance = root.querySelector('.dj-product-assistance');
  const selectionHeading = element('h2', '', copy.heading); selectionHeading.tabIndex = -1;
  const selectionGeometry = element('div', 'dj-dp-selection-geometry', initialGeometry);
  if (assurance) {
    assurance.classList.add('dj-dp-selection');
    const heading = element('div', 'dj-dp-selection-heading');
    heading.append(selectionHeading, element('span', 'dj-dp-no-vehicle', copy.no_vehicle));
    const check = element('p', 'dj-dp-selection-check', copy.check);
    const icon = assurance.querySelector('svg');
    if (icon) check.prepend(icon);
    assurance.replaceChildren(heading, selectionGeometry, element('p', 'dj-dp-selection-note', copy.compatibility), check);
  }
  prepareMedia(root, copy);
  orderPurchaseBlocks(root, form);
  const updateTotal = deliveredTotal(root, form, copy);
  const input = form.querySelector('[name="quantity"]');
  const quantityWrapper = input?.closest('.quantity-selector-wrapper');
  if (input && quantityWrapper) {
    if (!input.id) input.id = `dj-dp-quantity-${form.id || 'product'}`;
    const label = element('label', 'dj-vp-quantity-label', copy.quantity);
    label.htmlFor = input.id; quantityWrapper.prepend(label);
    input.setAttribute('aria-label', copy.quantity);
    form.querySelector('.quantity-label')?.setAttribute('hidden', '');
  }
  let addText = form.querySelector('.add-to-cart-text__content');
  if (!addText) {
    const addButton = form.querySelector('button[type="submit"]');
    const text = [...(addButton?.childNodes || [])].find(node => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
    if (text) {
      addText = element('span', 'dj-dp-add-label', text.textContent.trim());
      text.replaceWith(addText);
    }
  }
  const originalAdd = addText?.textContent;
  let initializing = true;
  function presentation(box) {
    for (const price of box.querySelectorAll('.dj-vc-dimension > .dj-vc-price')) {
      if (!price.querySelector('.dj-dp-unit') && /€/.test(price.textContent)) price.append(element('small', 'dj-dp-unit', copy.per_wheel));
    }
    for (const option of box.querySelectorAll('.dj-vc-axle option')) {
      option.textContent = option.textContent.replace(/ · [\d.,\s]+ € HT$/, '');
    }
  }
  const manual = createConfiguration({ root, form, ...configuration,
    getProperties: () => ({}), activate: () => {}, onRender: presentation,
    onSelectionChange: selection => {
      selectionGeometry.replaceChildren();
      assurance?.classList.toggle('is-pending', !selection);
      if (addText) addText.textContent = selection ? originalAdd || copy.add : copy.validate;
      if (!selection) {
        selectionGeometry.textContent = copy.pending;
        updateTotal();
        return;
      }
      const pair = selection.rows.length === 2;
      selectionGeometry.classList.toggle('is-pair', pair);
      selection.rows.forEach((row, index) => {
        const label = pair ? `${index ? copy.rear : copy.front} · 2 × ${dimensionLabel(row)}` : dimensionLabel(row);
        selectionGeometry.append(element('p', '', label));
      });
      const orderSummary = root.querySelector('.dj-vc-order-summary');
      const totalText = orderSummary?.querySelector(':scope > strong');
      const deliveryText = totalText?.nextElementSibling;
      if (totalText && deliveryText?.tagName === 'SPAN') {
        const subtotalLine = element('div', 'dj-dp-subtotal');
        totalText.before(subtotalLine);
        subtotalLine.append(totalText, deliveryText);
      }
      if (!pair) {
        const price = orderSummary?.querySelector('p.dj-vc-price');
        if (price) {
          const amount = (selection.rows[0].price / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          price.replaceChildren(element('span', 'dj-dp-price-amount', `${amount} €`), document.createTextNode(' '), element('small', 'dj-dp-price-suffix', `HT ${copy.per_wheel}`));
        }
      }
      const stock = element('div', 'dj-dp-availability');
      for (const [index, row] of selection.rows.entries()) {
        const prefix = pair ? `${index ? copy.rear : copy.front} · ` : '';
        stock.append(element('span', '', prefix + (row.stock === null ? copy.stock_check : copy.stock_count.replace('__count__', row.stock))));
      }
      orderSummary?.append(stock);
      updateTotal();
      if (!initializing && assurance) scrollToConfiguration(selectionHeading);
    },
  });
  manual.setActive(true);
  const pair = root.querySelector('[data-dj-configuration-mode="pair"]');
  if (!pair || pair.hidden) guideNote.textContent = copy.guide_single;
  const rule = root.querySelector('.dj-product-trust-strip > div:nth-child(2) span');
  if (rule) rule.textContent = pair && !pair.hidden ? copy.four_pair : copy.four_identical;
  Promise.resolve(manual.selectCurrent?.()).finally(() => { initializing = false; });
}

function start(scope = document) {
  for (const root of scope.querySelectorAll('.product-information')) init(root);
}
start();
document.addEventListener('shopify:section:load', event => start(event.target));
